import {
  HEALTHCARE_TEST_TYPES,
  PROJECT_STATUSES,
  PROJECT_STATUSES_LABELS,
} from 'Constants';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { generatePath, useParams } from 'react-router-dom';

import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import { Button, Grid, Stack, Typography } from '@mui/material';

import authentication from 'Api/authentication';
import originpoints from 'Api/originpoints';
import projects from 'Api/projects';
import testsApi from 'Api/tests';

import BreadcrumbsProjects from 'Components/Breadcrumbs/BreadcrumbsProjects';
import Title from 'Components/Mix/Title';
import EditProjectDetailsBtn from 'Components/SingleProject/Buttons/EditProjectDetailsBtn';
import EditProjectStatusBtn from 'Components/SingleProject/Buttons/EditProjectStatusBtn';
import EditTestDetailsModal from 'Components/SingleProject/Modals/EditTestDetailsModal';
import ProjectTagsTable from 'Components/SingleProject/Tables/ProjectTagsTable';
import ProjectTestsTable from 'Components/SingleProject/Tables/ProjectTestsTable';
import TagsSelectorTable from 'Components/SingleProject/Tables/TagsSelectorTable';
import { ProjectContextProvider } from 'Components/SingleProject/context';
import ErrorMessage from 'Components/UI/ErrorMessage';
import Loading from 'Components/UI/Loading';

import {
  ACCESS_LEVEL_ADMIN,
  ACCESS_LEVEL_EDITOR,
  ROLE_PARTNER,
  ROLE_SAFETRACES,
  isSafetracesAdmin,
  isViewer,
} from 'Config/roles';
import { ROUTE_PROJECT_DOWNLOADS } from 'Config/routes';

import { useIsMounted } from 'Context';

import { getDataFromResponse, getUserData, isValidResponse } from 'Utils';

export default function ProjectDetails() {
  const { projectId } = useParams();

  const [projectData, setProjectData] = useState(null);
  const [projectTagsCommon, setProjectTagsCommon] = useState(null);
  const [projectTagsHealthcare, setProjectTagsHealthcare] = useState(null);
  const [extendedTagsFetched, setExtendedTagsFetched] = useState(false);
  const [tagsFetching, setTagsFetching] = useState(false);

  const [projectCustomers, setProjectCustomers] = useState([]);
  const [projectPartners, setProjectPartners] = useState([]);
  const [cantDeleteTests, setCantDeleteTests] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [mainError, setMainError] = useState('');
  const mounted = useIsMounted();

  const { roleName, accessLevel } = getUserData();
  const editPermission =
    !!~[ACCESS_LEVEL_ADMIN, ACCESS_LEVEL_EDITOR].indexOf(accessLevel) &&
    !!~[ROLE_SAFETRACES, ROLE_PARTNER].indexOf(roleName);
  const editProjectDetailsPermission =
    !!~[ACCESS_LEVEL_ADMIN, ACCESS_LEVEL_EDITOR].indexOf(accessLevel) &&
    !!~[ROLE_SAFETRACES].indexOf(roleName);

  const editProjectStatusForwardPermission =
    !!~[ACCESS_LEVEL_ADMIN, ACCESS_LEVEL_EDITOR].indexOf(accessLevel) &&
    !!~[ROLE_SAFETRACES, ROLE_PARTNER].indexOf(roleName);
  const editProjectStatusBackwardPermission =
    !!~[ACCESS_LEVEL_ADMIN, ACCESS_LEVEL_EDITOR].indexOf(accessLevel) &&
    !!~[ROLE_SAFETRACES].indexOf(roleName);

  useEffect(() => {
    fetchProject();
    fetchTags();
    fetchCustomers();
    fetchPartners();
  }, []);

  const fetchProject = useCallback(async () => {
    setIsLoading(true);
    try {
      const statusUpdateResponse = await projects.autoUpdateStatus(projectId);
      if (!isValidResponse(statusUpdateResponse)) {
        throw new Error('Failed to update project status');
      }

      const [response, testNumbersResponse] = await Promise.all([
        projects.getOneProject(projectId, true),
        testsApi.getDisplayNumbersProject(projectId),
      ]);
      if (isValidResponse(response) && isValidResponse(testNumbersResponse)) {
        if (mounted.current) {
          const testNumbersConfig = getDataFromResponse(testNumbersResponse);
          const project = getDataFromResponse(response);
          if (Array.isArray(project.tests)) {
            for (let test of project.tests) {
              test.orderNumber = testNumbersConfig[test.testid];
            }
          }
          setProjectData(project);
          const cantDelete =
            project.status &&
            !~[PROJECT_STATUSES.NEW, PROJECT_STATUSES.IN_PROGRESS].indexOf(
              project.status,
            );

          setCantDeleteTests(cantDelete);
        }
      }
    } catch (e) {
      if (mounted.current) {
        setProjectData(null);
      }
      console.log(e.toString());
    } finally {
      if (mounted.current) {
        setIsLoading(false);
      }
    }
  }, [projectId]);

  const fetchTags = useCallback(
    async (includeAllTags = false) => {
      setTagsFetching(true);

      const response = await originpoints.getAllOriginPointsForProject(
        projectId,
        includeAllTags,
      );
      if (isValidResponse(response)) {
        const data = response.data.data;
        const [_tagsCommon, _tagsHealthcare] = getParsedTags(data);

        setProjectTagsCommon(_tagsCommon);
        setProjectTagsHealthcare(_tagsHealthcare);
        setExtendedTagsFetched(includeAllTags);
      }

      setTagsFetching(false);
    },
    [projectId],
  );

  const fetchCustomers = async () => {
    if (!isSafetracesAdmin()) return;
    try {
      const response = await authentication.getCustomers();
      if (isValidResponse(response) && response.data.data.length) {
        const customers = [];
        response.data.data.forEach((value) => {
          customers.push({ name: value.companyname });
        });

        setProjectCustomers(customers);
      }
    } catch (err) {
      console.log('fetchCustomers', err);
    }
  };

  const fetchPartners = async () => {
    if (!isSafetracesAdmin()) return;
    try {
      const response = await authentication.getPartners();
      if (isValidResponse(response) && response.data.data.length) {
        const partners = [{ name: 'Safetraces' }];
        response.data.data.forEach((value) => {
          partners.push({ name: value.companyname });
        });
        setProjectPartners(partners);
      }
    } catch (err) {
      console.log('fetchPartners', err);
    }
  };

  const getParsedTags = (projectTags) => {
    let tagsTmpCommon = {};
    let tagsTmpHealthcare = {};
    projectTags.forEach((obj) => {
      const { opid, tags, tagLotNumber, tagLotNumbersList, testtype } = obj;
      const tagsTmp = Object.values(HEALTHCARE_TEST_TYPES).includes(testtype)
        ? tagsTmpHealthcare
        : tagsTmpCommon;
      tags.forEach(function (tag) {
        let tagName = tag.tag;

        if (!tagName.startsWith('')) {
          return;
        }

        if (tagsTmp[tagName] === undefined || null) {
          tagsTmp[tagName] = [];
        }

        let item = {
          opid: opid,
          tag: tag.tag,
          tagLotNumber: null,
          expirationDate: null,
        };

        if (tagLotNumber !== undefined) {
          tagLotNumber.forEach(function (tagLot) {
            if (tagName === tagLot.tag) {
              if (tagLot.lotNumber !== 'null') {
                item.tagLotNumber = tagLot.lotNumber;
              }
              if (tagLot.expirationDate !== 'null') {
                item.expirationDate = tagLot.expirationDate;
              }
              item.tag = tagLot.tag;
            }
          });
        }
        // Adding tag lot numbers by tag name
        item.numbers = tagLotNumbersList?.filter((i) => i.tag === tagName);
        item.tagLotNumberIsSterile = item.tagLotNumber
          ? tagLotNumbersList?.filter(
              (i) => i.tag === tagName && i.taglotnumber === item.tagLotNumber,
            )[0]?.issterile
          : false;
        tagsTmp[tagName].push(item);
      });
    });

    // Order object by key names
    return [
      Object.keys(tagsTmpCommon)
        .sort()
        .reduce((obj, key) => {
          obj[key] = tagsTmpCommon[key];
          return obj;
        }, {}),
      Object.keys(tagsTmpHealthcare)
        .sort()
        .reduce((obj, key) => {
          obj[key] = tagsTmpHealthcare[key];
          return obj;
        }, {}),
    ];
  };

  const contextValue = useMemo(() => {
    return {
      reFetchProject: fetchProject,
      refetchProjectTags: fetchTags,
      projectId,
      projectStatus: projectData?.status,
      testAmountChangeHandler: () => {
        fetchProject();
        fetchTags();
      },
    };
  }, [projectId, fetchProject, projectData?.status]);

  return (
    <ProjectContextProvider value={contextValue}>
      <EditTestDetailsModal />
      {!!mainError && (
        <ErrorMessage
          message={mainError}
          handleCloseErrorAlert={() => setMainError(null)}
        />
      )}
      {!!isLoading && <Loading />}
      {!isLoading && projectData !== null && (
        <div style={{ width: '100%' }}>
          <BreadcrumbsProjects currentProjectName={projectData.name} />
          <Stack direction="column">
            <Title text={`${projectData.name} (Id ${projectData.id})`} />
            <Grid container spacing={2}>
              {!isViewer() && (
                <Grid item xs={6}>
                  <Button
                    variant="contained"
                    href={generatePath(ROUTE_PROJECT_DOWNLOADS, {
                      projectId: projectData.id,
                    })}
                    endIcon={<FolderOutlinedIcon />}
                    style={{ width: '160px' }}
                    size="small"
                  >
                    downloads
                  </Button>
                </Grid>
              )}
              {editProjectDetailsPermission && (
                <Grid item xs={6}>
                  <Stack direction="row" justifyContent="end">
                    <EditProjectDetailsBtn
                      sx={{ mt: 1 }}
                      projectId={projectId}
                      afterAdd={fetchProject}
                      projectData={projectData}
                      projectCustomers={projectCustomers}
                      projectPartners={projectPartners}
                      hasTestPackages={projectData.hasTestPackages}
                    />
                  </Stack>
                </Grid>
              )}
            </Grid>
            <Grid container spacing={2} mt={2} mb={2}>
              <Grid item xs={6} md={4}>
                <Typography variant={'body1'}>
                  <b>Name:</b> {projectData.name}
                </Typography>
              </Grid>
              <Grid item xs={6} md={4}>
                <Stack direction="row" alignItems="center">
                  <Typography variant={'body1'}>
                    <b>Status:</b> {PROJECT_STATUSES_LABELS[projectData.status]}
                  </Typography>
                  {editProjectStatusBackwardPermission &&
                  !!~[
                    PROJECT_STATUSES.EXECUTION_READY,
                    PROJECT_STATUSES.READY_FOR_LAB,
                    PROJECT_STATUSES.EXECUTED,
                    PROJECT_STATUSES.PUBLISHED,
                  ].indexOf(projectData.status) ? (
                    <EditProjectStatusBtn
                      sx={{ ml: 1 }}
                      projectId={projectId}
                      reFetchProject={fetchProject}
                      currentStatus={projectData.status}
                      flowType="backwards"
                    />
                  ) : null}
                  {isSafetracesAdmin() &&
                  projectData.status === PROJECT_STATUSES.IN_PROGRESS ? (
                    <EditProjectStatusBtn
                      sx={{ ml: 1 }}
                      projectId={projectId}
                      reFetchProject={fetchProject}
                      currentStatus={projectData.status}
                      flowType="backwards"
                    />
                  ) : null}
                  {editProjectStatusForwardPermission &&
                  !!~[
                    PROJECT_STATUSES.IN_PROGRESS,
                    PROJECT_STATUSES.EXECUTION_READY,
                    PROJECT_STATUSES.READY_FOR_LAB,
                    PROJECT_STATUSES.RESULTS_GENERATED,
                  ].indexOf(projectData.status) ? (
                    <EditProjectStatusBtn
                      sx={{ ml: 1 }}
                      projectId={projectId}
                      reFetchProject={fetchProject}
                      currentStatus={projectData.status}
                    />
                  ) : null}
                </Stack>
              </Grid>
              <Grid item xs={6} md={4}>
                <Typography variant={'body1'}>
                  <b>Address:</b> {projectData.address}
                </Typography>
              </Grid>
              <Grid item xs={6} md={4}>
                <Typography variant={'body1'}>
                  <b>Customer:</b> {projectData.endCustomer}
                </Typography>
              </Grid>
              <Grid item xs={6} md={4}>
                <Typography variant={'body1'}>
                  <b>Description:</b> {projectData.description}
                </Typography>
              </Grid>
              <Grid item xs={6} md={4}>
                <Typography variant={'body1'}>
                  <b>Partner:</b> {projectData.partners}
                </Typography>
              </Grid>
              <Grid item xs={6} md={4}>
                <Typography variant={'body1'}>
                  <b>Test Package:</b> {projectData.testPackageName}
                </Typography>
              </Grid>
              <Grid item xs={6} md={4}>
                <Typography variant={'body1'}>
                  <b>Site Contract:</b> {projectData.siteContractName}
                </Typography>
              </Grid>
              <Grid item xs={6} md={4}>
                <Typography variant={'body1'}>
                  <b>Contract:</b> {projectData.contractName}
                </Typography>
              </Grid>
              <Grid item xs={6} md={4}>
                <Typography variant={'body1'}>
                  <b>Portfolio:</b> {projectData.portfolioName}
                </Typography>
              </Grid>
            </Grid>
            <ProjectTestsTable
              tests={projectData.tests}
              cantDeleteTests={cantDeleteTests}
              canAddTests={
                !~[
                  PROJECT_STATUSES.READY_FOR_LAB,
                  PROJECT_STATUSES.RESULTS_GENERATED,
                  PROJECT_STATUSES.PUBLISHED,
                ].indexOf(projectData.status)
              }
              canEditTests={
                editPermission &&
                !~[PROJECT_STATUSES.PUBLISHED].indexOf(projectData.status)
              }
              buildings={projectData.buildings}
              projectCompleted={projectData.testCompleted}
            />
            <Stack sx={{ my: 4 }} direction="column">
              <Typography variant={'h5'} sx={{ mb: 2 }}>
                Tag Selection
              </Typography>
              <TagsSelectorTable
                tests={projectData.tests || []}
                updateProjectData={() => {
                  fetchProject();
                  fetchTags();
                }}
                canEditTests={
                  editPermission &&
                  !~[PROJECT_STATUSES.PUBLISHED].indexOf(projectData.status)
                }
              />
            </Stack>
            <Stack direction="row" style={{ width: '100%' }}>
              {!!projectTagsCommon && Object.keys(projectTagsCommon).length ? (
                <Stack direction="column" style={{ width: '50%' }}>
                  <Typography variant={'h5'} sx={{ mt: 4, mb: 2 }}>
                    Tags
                  </Typography>
                  <ProjectTagsTable
                    tags={projectTagsCommon}
                    fetchTags={fetchTags}
                    fetchTagsExtended={() => fetchTags(true)}
                    tagsFetching={tagsFetching}
                    extendedTagsFetched={extendedTagsFetched}
                    editDisabled={
                      !!~[PROJECT_STATUSES.PUBLISHED].indexOf(
                        projectData.status,
                      )
                    }
                  />
                </Stack>
              ) : null}
              {!!projectTagsHealthcare &&
              Object.keys(projectTagsHealthcare).length ? (
                <Stack direction="column" style={{ width: '50%' }}>
                  <Typography variant={'h5'} sx={{ mt: 4, mb: 2 }}>
                    Tags (CET)
                  </Typography>
                  <ProjectTagsTable
                    tags={projectTagsHealthcare}
                    fetchTags={fetchTags}
                    fetchTagsExtended={() => fetchTags(true)}
                    tagsFetching={tagsFetching}
                    extendedTagsFetched={extendedTagsFetched}
                    editDisabled={
                      !!~[PROJECT_STATUSES.PUBLISHED].indexOf(
                        projectData.status,
                      )
                    }
                  />
                </Stack>
              ) : null}
            </Stack>
          </Stack>
        </div>
      )}
    </ProjectContextProvider>
  );
}
